// English strings (source language): the landing page every signed-out visitor sees (ui/screens/welcome.ts).

const en = {
  nav: { together: 'Multiplayer', modes: 'Modes', training: 'Training', gear: 'Equipment' },
  navLabel: 'Page sections',
  language: 'Language',
  signIn: 'Sign in',
  createAccount: 'Create an account',
  hero: {
    eyebrow: 'Multiplayer indoor cycling',
    titleA: 'Race your friends.',
    titleB: 'Live, from home.',
    lead: 'Gruvetto is an online cycling game for smart trainers. Share a code and your friends join you on the same road, in real time. Your watts move your rider, and the server keeps every result fair.',
    cta: 'Create your account',
    signIn: 'Sign in',
    points: ['Nothing to install', 'Runs in Chrome and Edge', 'Bluetooth smart trainers'],
  },
  /** The hero's figures: the field size is an engine limit; the rest are words, since players keep adding content. */
  stats: {
    riders: 'riders in one race',
    live: ['Live', 'with friends, one code to join'],
    open: ['Open', 'tracks, modes and workouts made by players'],
    real: ['Real', 'watts from your smart trainer'],
  },
  together: {
    eyebrow: 'Multiplayer',
    title: 'One road. Everyone on it.',
    lead: 'Open a room, send the code, and race from wherever your trainers are. Bots fill the empty places, so a race is never short of riders.',
    steps: [
      ['Open a room', 'Pick a mode and a track. The room gets a four-letter code.'],
      ['Share the code', 'Friends type it in to join, or to watch and cheer.'],
      ['Race live', 'Everyone on the same road, with chat and real drafting.'],
    ],
    features: {
      fair: ['Fair results', 'Every race runs on the game server and its results are signed. Nobody can fake a time.'],
      events: ['Events and tours', 'Scheduled starts, stage races with jerseys and a time for every stage.'],
      clubs: ['Clubs and teams', 'Your club with its own kit, and team time trials ridden together.'],
      supporters: ['Supporters', 'Friends without a trainer watch the race with the code and cheer you on.'],
      boards: ['Leaderboards', 'Levels, weekly distance and records on every track.'],
      chat: ['Chat', 'Talk in the room before the start and during the race.'],
    },
    alt: 'A bunch of riders on a mountain road at golden hour',
  },
  modes: {
    eyebrow: 'Modes',
    title: 'Six ways to race, and counting',
    lead: 'Six official modes, and more invented by players in the workshop. Bots are matched to your watts per kilo, so every race stays close.',
    list: {
      race: 'A full race with item boxes. Tactics, drafting and a sprint for the line.',
      elim: 'Each time the leader completes a lap, the last rider is out.',
      tt: 'No items and no drafting. Your power against the clock.',
      ttt: 'Teams of four start a minute apart. The clock stops on the second rider.',
      ghost: 'Race your own best run, second by second.',
      free: 'Ride in a group at the pace you choose, with no finish line.',
    },
    alt: 'The front of the bunch, filmed from the road',
  },
  training: {
    eyebrow: 'Training',
    title: 'Every watt is real',
    lead: 'Your smart trainer follows the gradient and the draft. Structured sessions hold your target power in ERG mode.',
    points: [
      'Workouts, FTP and ramp tests, and plans that adapt to how you feel.',
      'Fitness, fatigue, power curve and zones after every ride.',
      'Import and export .zwo workouts, and send your rides to Strava.',
    ],
    alt: 'A workout in progress: the interval bar above the road',
  },
  progress: {
    eyebrow: 'Progress',
    title: 'Built to keep you riding',
    lead: 'Every ride counts toward your level, your fitness and your records, all kept on your account.',
    points: [
      'Levels, achievements and unlocks, computed on the server.',
      'A rider and a kit of your own, from helmet to frame.',
      'Legendary challenges: Everesting, or 24 hours around the Observatoire.',
      'A workshop to create tracks, modes and workouts, and share them.',
    ],
    alt: 'A rider in the wardrobe',
  },
  gear: {
    eyebrow: 'Equipment',
    title: 'Works with your equipment',
    lead: 'Connect everything in one Bluetooth search. Riding needs a smart trainer or a power meter.',
    items: {
      trainer: ['Smart trainers', 'Bluetooth FTMS: gradient, ERG mode and power'],
      power: ['Power meters', 'Pedals, cranks or hubs'],
      classic: ['Classic trainers', 'With a speed sensor: power is estimated'],
      hr: ['Heart-rate monitors', 'Straps and armbands'],
      cadence: ['Speed and cadence', 'Sensors on the bike'],
      controller: ['Zwift Ride controllers', 'Steer and use items from the bars'],
    },
  },
  join: {
    eyebrow: 'Get started',
    title: 'Your first race is minutes away',
    lead: 'Your account keeps your progress on every device and puts your name on the leaderboards.',
    steps: [
      ['Create your account', 'A rider name, an email and a password.'],
      ['Connect your trainer', 'One Bluetooth search finds all your devices.'],
      ['Join your friends', 'Type their room code, or open a room and share yours.'],
    ],
  },
  footer: {
    tagline: 'Multiplayer cycling on your smart trainer.',
    rights: (year: number) => `© ${year} Gruvetto`,
  },
};
export default en;
